import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import Html from 'react-pdf-html';

import {
  styles,
  Header,
  Footer,
  GrayLine,
  StandardPDFColors,
} from './common/CommonPDFComponents';
import { formattedTextStyles } from './ReportMessagesPDF';

import { ReportDetailsProp } from '../../../components/viewReport/reportDetails';
import { getConfideFormattedDateTime } from '../helpers';

import { sanitizeHTMLStringForPDFRender } from '~/utils/pdfs';

interface HeaderInformation {
  lastUpdated: string;
  status: string;
}

interface ReportInformationProps {
  userName: string;
  reportType: string;
  countryName: string;
  createdAt: string;
}

interface ReportDescriptionInformation {
  content: string;
}

const documentStyles = StyleSheet.create({
  detailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: '23px',
  },
  updatedStatusContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '20px',
  },
  updatedStatus: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  information: {
    ...styles.sizeBody,
    marginTop: 16,
    gap: '4px',
    textTransform: 'uppercase',
  },
});

const ReportDetailsHeader = ({ lastUpdated, status }: HeaderInformation) => (
  <>
    <View style={documentStyles.detailsHeader}>
      <Text style={styles.sizeSubHeading}>Report Details</Text>
      <View style={styles.spacer} />
      <View style={documentStyles.updatedStatusContainer}>
        <View style={documentStyles.updatedStatus}>
          <Text
            style={{
              color: StandardPDFColors.gray,
              ...styles.sizeCaption,
            }}
          >
            Last Updated:
          </Text>
          <Text
            style={{
              ...styles.medium,
              ...styles.sizeBody,
            }}
          >
            {lastUpdated}
          </Text>
        </View>
        <View style={documentStyles.updatedStatus}>
          <Text
            style={{
              color: StandardPDFColors.gray,
              ...styles.sizeCaption,
            }}
          >
            Report Status:
          </Text>
          <Text
            style={{
              ...styles.medium,
              ...styles.sizeBody,
            }}
          >
            {status}
          </Text>
        </View>
      </View>
    </View>
    <View style={{ marginTop: 6 }}>
      <GrayLine />
    </View>
  </>
);

const ReportInformation = ({
  userName,
  reportType,
  countryName,
  createdAt,
}: ReportInformationProps) => (
  <View style={documentStyles.information}>
    <Text>{userName}</Text>
    <Text>{reportType}</Text>
    <Text>{countryName}</Text>
    <Text>{getConfideFormattedDateTime(createdAt)}</Text>
  </View>
);

const ReportDescription = ({ content }: ReportDescriptionInformation) => (
  <View style={{ marginTop: 16, gap: '8px' }}>
    <Text style={styles.sizeSubHeading}>Description</Text>
    <Html
      stylesheet={formattedTextStyles}
      style={{
        fontSize: 12,
        color: StandardPDFColors.descriptionTextGray,
      }}
    >
      {sanitizeHTMLStringForPDFRender(content)}
    </Html>
  </View>
);

const getReportDocument = (details: ReportDetailsProp) => (
  <Document>
    <Page size='A4' style={styles.page} wrap>
      <Header />
      <View style={styles.main}>
        <Text
          style={{
            ...styles.bold,
            ...styles.sizeHeading,
            textTransform: 'uppercase',
          }}
        >
          {details.tenantName}
        </Text>
        <ReportDetailsHeader
          lastUpdated={details.formattedUpdatedAt}
          status={details.statusName}
        />
        <ReportInformation
          userName={details.userName}
          reportType={details.reportTypeName}
          countryName={details.country?.name ?? 'Unknown'}
          createdAt={details.createdAt}
        />
        <ReportDescription content={details.description} />
      </View>
      <Footer />
    </Page>
  </Document>
);

const generateViewReportPDF = async (details: ReportDetailsProp) => {
  const generatedPDF = getReportDocument(details);
  const pdfBlob = await pdf(generatedPDF).toBlob();
  return pdfBlob;
};

export { generateViewReportPDF };
